import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Rating,
  Avatar,
  Divider,
  TextField,
  Button,
  Card,
  CardMedia,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Comment, Star } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { Helper } from "../../tools/Helper";
import { api_Routes } from "../../api_Routes";
import { enqueueSnackbar } from "notistack";

const AdDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoadingRating, setisloadingreting] = useState(false);
  const [ad, setAd] = useState(null);
  const { Adid } = useParams();
  const [review, setReview] = useState({
    rate: 0,
    description: "",
    subject: "",
  });

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setisloadingreting(true);
    const formData = new FormData();
    formData.append("rate", review.rate);
    formData.append("description", review.description);
    formData.append("subject", review.subject);
    formData.append("reseller_ad_id", Adid);
    const { response, message } = await Helper.Post({
      url: api_Routes.ResellerAd.Review,
      data: formData,
      hasToken: true,
    });
    if (response) {
      setisloadingreting(false);
      getAd();
      setReview({ rate: 0, description: "", subject: "" });
      enqueueSnackbar(message, {
        variant: "success",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });
    } else {
      let errorMessage = "";
      if (typeof message === "string") {
        errorMessage = message;
      } else if (typeof message === "object") {
        errorMessage = Object.values(message).flat().join(", ");
      }
      enqueueSnackbar(errorMessage, {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });
    }
    setisloadingreting(false);
  };
  const getAd = async () => {
    const { response, message } = await Helper.Get({
      url: `${api_Routes.ResellerAd.GetOne}/${Adid}`,
      hasToken: true,
    });
    if (response) {
      setAd(response.data);
    }
  };
  useEffect(() => {
    getAd();
  }, [Adid]);
  if (!ad) return <div>Loading...</div>;

  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", p: 3 }}>
      {/* Title Section */}
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        {ad.name}
      </Typography>

      {/* Rating and Reviews Summary */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Rating
          value={ad.rating}
          precision={0.5}
          readOnly
          size="large"
          sx={{ mr: 2 }}
        />
        <Typography variant="h6" color="text.secondary">
          ({ad.reviews?.length} reviews)
        </Typography>
      </Box>

      {/* Main Photo */}
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardMedia
          component="img"
          height="500"
          image={ad.url}
          alt={ad.name}
          sx={{
            objectFit: "contain",
            ...(isMobile && { height: 300 }),
          }}
        />
      </Card>

      {/* Description */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Description
        </Typography>
        <div dangerouslySetInnerHTML={{ __html: ad.details }} />
      </Box>

      {/* Reviews Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Customer Reviews
        </Typography>

        {/* Existing Reviews */}
        <List sx={{ mb: 4 }}>
          {ad.reviews?.map((review, index) => (
            <React.Fragment key={index}>
              <ListItem alignItems="flex-start">
                <ListItemAvatar>
                  <Avatar>{review.customer.name[0]}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {review.customer.name}
                      </Typography>
                      <Rating
                        value={review.rate}
                        readOnly
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </>
                  }
                  secondary={
                    <>
                      <Typography
                        variant="body1"
                        sx={{ mt: 1, color: "text.primary" }}
                      >
                        {review.subject}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: "text.secondary" }}
                      >
                        {review.description}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
              <Divider variant="inset" component="li" />
            </React.Fragment>
          ))}
        </List>

        {/* Add Review Form */}
        <Card sx={{ p: 3, bgcolor: "background.paper" }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Write a Review
          </Typography>
          <form onSubmit={handleSubmitReview}>
            <TextField
              fullWidth
              label="Review Title"
              value={review.subject}
              onChange={(e) =>
                setReview({ ...review, subject: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              <Typography component="legend">Your Rating</Typography>
              <Rating
                value={review.rate}
                onChange={(_, value) => setReview({ ...review, rate: value })}
                size="large"
                sx={{ mt: 1 }}
              />
            </Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Your Review"
              value={review.description}
              onChange={(e) =>
                setReview({ ...review, description: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<Comment />}
            >
              {isLoadingRating ? "Submitting..." : "Submit Your Review"}
            </Button>
          </form>
        </Card>
      </Box>
    </Box>
  );
};

export default AdDetails;
